import { Facebook, Twitter, Linkedin, Mail, Copy, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

interface SocialShareProps {
  url?: string;
  title: string;
  description?: string;
  variant?: "inline" | "floating";
  showLabel?: boolean;
}

const SocialShare = ({
  url = typeof window !== "undefined" ? window.location.href : "",
  title,
  description = "",
  variant = "inline",
  showLabel = false,
}: SocialShareProps) => {
  const shareLinks = [
    {
      name: "Facebook",
      icon: Facebook,
      getUrl: () => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    },
    {
      name: "Twitter",
      icon: Twitter,
      getUrl: () => `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      getUrl: () => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    },
    {
      name: "Email",
      icon: Mail,
      getUrl: () => `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${description}\n\n${url}`)}`,
    },
  ];

  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    toast({
      title: "Link copied!",
      description: "The link has been copied to your clipboard.",
    });
  };

  if (variant === "floating") {
    return (
      <div className="fixed right-4 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col gap-2 bg-background/80 backdrop-blur p-2 rounded-lg shadow-lg border">
        {shareLinks.map((link) => (
          <Button
            key={link.name}
            variant="ghost"
            size="icon"
            onClick={() => window.open(link.getUrl(), "_blank")}
            title={`Share on ${link.name}`}
          >
            <link.icon className="h-4 w-4" />
          </Button>
        ))}
        <Button variant="ghost" size="icon" onClick={handleCopy} title="Copy link">
          <Copy className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {showLabel && (
        <span className="text-sm text-muted-foreground flex items-center gap-1">
          <Share2 className="h-4 w-4" /> Share:
        </span>
      )}
      {shareLinks.map((link) => (
        <Button
          key={link.name}
          variant="outline"
          size="sm"
          onClick={() => window.open(link.getUrl(), "_blank")}
          className="gap-2"
        >
          <link.icon className="h-4 w-4" />
          {showLabel && <span className="hidden sm:inline">{link.name}</span>}
        </Button>
      ))}
      <Button variant="outline" size="sm" onClick={handleCopy} className="gap-2">
        <Copy className="h-4 w-4" />
        {showLabel && <span className="hidden sm:inline">Copy</span>}
      </Button>
    </div>
  );
};

export default SocialShare;
